import axios from "axios";
import Cookies from "js-cookie";

const AICHATBOT_HOST = "aichatbot-326159028339.asia-southeast1.run.app";
const HR_API_HOST = "api.oneplace.hr";
const REFRESH_ENDPOINT = "/v1/auth/refresh";

const TOKEN_KEY = "chatToken";
const REFRESH_TOKEN_KEY = "chatRefreshToken";

const isAichatbotUrl = (url) => {
  try {
    return new URL(url).host === AICHATBOT_HOST;
  } catch {
    return typeof url === "string" && url.includes(AICHATBOT_HOST);
  }
};

const isHrApiUrl = (url) => {
  try {
    return new URL(url, window.location.origin).host === HR_API_HOST;
  } catch {
    return typeof url === "string" && url.includes(HR_API_HOST);
  }
};

const storeRefreshedToken = (token) => {
  if (!token || typeof token !== "string") return;
  Cookies.set(TOKEN_KEY, token, { expires: 30 });
};

// Persist the access + refresh token pair returned by an hrSystemServer login.
// Call this from login handlers so the refresh interceptor has a refresh token.
export const setChatTokens = ({ token, refreshToken }) => {
  if (token) Cookies.set(TOKEN_KEY, token, { expires: 30 });
  if (refreshToken) Cookies.set(REFRESH_TOKEN_KEY, refreshToken, { expires: 30 });
};

export const clearChatTokens = () => {
  Cookies.remove(TOKEN_KEY);
  Cookies.remove(REFRESH_TOKEN_KEY);
};

// Read X-Refreshed-Token from a Fetch Response and persist it.
export const captureRefreshedTokenFromFetch = (response) => {
  if (!response?.headers?.get) return;
  storeRefreshedToken(response.headers.get("X-Refreshed-Token"));
};

// ---- hrSystemServer access-token refresh (401 -> /v1/auth/refresh -> retry) ----
let isRefreshing = false;
let refreshSubscribers = [];
const subscribeToRefresh = (cb) => refreshSubscribers.push(cb);
const notifySubscribers = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const attemptTokenRefresh = async () => {
  const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;
  try {
    const { data } = await axios.post(
      `https://${HR_API_HOST}${REFRESH_ENDPOINT}`,
      { refreshToken },
      { _skipAuthRefresh: true }
    );
    if (data?.token) {
      setChatTokens({ token: data.token, refreshToken: data.refreshToken });
      return data.token;
    }
  } catch (e) {
    // fall through -> session expired
  }
  return null;
};

const handleSessionExpired = () => {
  clearChatTokens();
  window.dispatchEvent(new CustomEvent("auth:sessionExpired"));
  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign("/login");
  }
};

// Global axios interceptor:
//  - success: aichatbot responses carrying X-Refreshed-Token rotate the chatToken
//  - error 401 from hrSystemServer: refresh the access token once (queueing
//    concurrent failures), then retry the original request; on failure, redirect
//    to login. 403 is left untouched (authenticated-but-forbidden).
let installed = false;
export const installAichatbotTokenInterceptor = () => {
  if (installed) return;
  installed = true;
  axios.interceptors.response.use(
    (response) => {
      const url = response.config?.url || "";
      if (isAichatbotUrl(url)) {
        const fresh = response.headers?.["x-refreshed-token"];
        storeRefreshedToken(fresh);
      }
      return response;
    },
    async (error) => {
      const { config, response } = error;
      const url = config?.url || "";

      const isRefreshable =
        response?.status === 401 &&
        config &&
        !config._isAuthRetry &&
        !config._skipAuthRefresh &&
        isHrApiUrl(url) &&
        !url.includes(REFRESH_ENDPOINT);

      if (!isRefreshable) return Promise.reject(error);

      // A refresh is already in flight: queue this request until it resolves.
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeToRefresh((token) => {
            if (!token) {
              reject(error);
              return;
            }
            config._isAuthRetry = true;
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
            resolve(axios(config));
          });
        });
      }

      isRefreshing = true;
      const newToken = await attemptTokenRefresh();
      isRefreshing = false;
      notifySubscribers(newToken);

      if (newToken) {
        config._isAuthRetry = true;
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${newToken}`;
        return axios(config);
      }

      handleSessionExpired();
      return Promise.reject(error);
    }
  );
};
